import { useEffect, useState } from "react";
import { Card, Col, Row, Spinner, Form, ButtonGroup, Button, Toast, ToastContainer } from "react-bootstrap";
import { getDashboardStats, getUsersByDateRange, getUserActivityByDateRange, getEmailStats } from "../services/apiService";
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import StatCard from "./components/StatCards";
import UserRegistrationChart from "./components/UserRegistrationChart";
import UserLoginActivityChart from "./components/UserLoginActivityChart";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [registrationChartData, setRegistrationChartData] = useState([]); // For New Users
  const [loginChartData, setLoginChartData] = useState([]); // For Logins

  // State for Registration Chart
  const [registrationTimeUnit, setRegistrationTimeUnit] = useState('daily');
  const [registrationDateRange, setRegistrationDateRange] = useState({
    start: format(subDays(new Date(), 6), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });

  // State for Login Chart
  const [loginTimeUnit, setLoginTimeUnit] = useState('daily');
  const [loginDateRange, setLoginDateRange] = useState({
    start: format(subDays(new Date(), 6), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });

  const [registeredUsers, setRegisteredUsers] = useState([]); // For New Users
  const [userActivity, setUserActivity] = useState([]); // For Logins
  const pageBackground = {
    minHeight: '100vh',
    background: '#f5f7fb',
    padding: '2.5rem 2rem',
    color: '#0f172a',
  };
  const panelStyle = {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)',
    borderRadius: '16px',
  };
  const ANNOUNCEMENT_KEY = "dashboardAnnouncementStartAt";
  const announcementText = "Heads up:Notifications Broadcaster has been added to the application. Use it carefully — it allows sending notifications to a single user, all users, or users filtered by category.";

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await getDashboardStats();
        const data = response.data;
        setStats({
          totalUsers: data.totalUsers,
          subscribedUsers: data.subscribedUsers,
          nonSubscribedUsers: data.totalUsers - data.subscribedUsers,
        });

        const emailResponse = await getEmailStats();
        setStats(prevStats => ({
          ...prevStats,
          emailStats: emailResponse.data,
        }));
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
        setError("Failed to load dashboard statistics.");
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const fetchRegisteredUsers = async () => {
      try {
        // setUsersLoading(true);
        // setUsersError(null);
        const response = await getUsersByDateRange(registrationDateRange.start, registrationDateRange.end); // Assuming this fetches registered users
        setRegisteredUsers(response.data);
      } catch (err) {
        console.error("Error fetching users by date range:", err);
        // setUsersError("Failed to load users.");
      } finally {
        // setUsersLoading(false);
      }
    };

    if (registrationDateRange.start && registrationDateRange.end) {
      fetchRegisteredUsers();
    }
  }, [registrationDateRange]);

  useEffect(() => {
    const fetchUserActivity = async () => {
      try {
        // setLoading(true); // Consider separate loading states if needed
        // setError(null);
        const response = await getUserActivityByDateRange(loginDateRange.start, loginDateRange.end);
        setUserActivity(response.data);
      } catch (err) {
        console.error("Error fetching user activity:", err);
        // setError("Failed to load user activity.");
      } finally {
        // setLoading(false);
      }
    };

    if (loginDateRange.start && loginDateRange.end) {
      fetchUserActivity();
    }
  }, [loginDateRange]);

  useEffect(() => {
    const processRegistrationChartData = () => {
      if (!registeredUsers || registeredUsers.length === 0) {
        setRegistrationChartData([]);
        return;
      }

      const dataMap = new Map();
      const startDate = startOfDay(new Date(registrationDateRange.start));
      const endDate = endOfDay(new Date(registrationDateRange.end));

      // Initialize dataMap with all dates in the range
      let currentDate = startDate;
      while (currentDate <= endDate) {
        let label;
        if (registrationTimeUnit === 'daily') {
          label = format(currentDate, 'MMM d');
        } else if (registrationTimeUnit === 'monthly') {
          label = format(currentDate, 'MMM yyyy');
        } else { // yearly
          label = format(currentDate, 'yyyy');
        }
        
        if (!dataMap.has(label)) {
          dataMap.set(label, { date: label, 'New Users': 0 });
        }

        currentDate = registrationTimeUnit === 'daily' ? subDays(currentDate, -1) : registrationTimeUnit === 'monthly' ? new Date(currentDate.setMonth(currentDate.getMonth() + 1)) : new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));
      }

      // Populate New Users from fetched data
      registeredUsers.forEach(user => {
        const createdAt = new Date(user.created_at);
        let label;
        if (registrationTimeUnit === 'daily') {
          label = format(createdAt, 'MMM d');
        } else if (registrationTimeUnit === 'monthly') {
          label = format(createdAt, 'MMM yyyy');
        } else { // yearly
          label = format(createdAt, 'yyyy');
        }

        if (dataMap.has(label)) {
          const entry = dataMap.get(label);
          entry['New Users'] += 1;
        }
      });

      setRegistrationChartData(Array.from(dataMap.values()));
    };
    processRegistrationChartData();
  }, [registeredUsers, registrationDateRange, registrationTimeUnit]);

  useEffect(() => {
    const processLoginActivityChartData = () => {
      if (!userActivity || userActivity.length === 0) {
        setLoginChartData([]);
        return;
      }

      const dataMap = new Map();
      const startDate = startOfDay(new Date(loginDateRange.start));
      const endDate = endOfDay(new Date(loginDateRange.end));

      // Initialize dataMap with all dates in the range
      let currentDate = startDate;
      while (currentDate <= endDate) {
        let label;
        if (loginTimeUnit === 'daily') {
          label = format(currentDate, 'MMM d');
        } else if (loginTimeUnit === 'monthly') {
          label = format(currentDate, 'MMM yyyy');
        } else { // yearly
          label = format(currentDate, 'yyyy'); // Corrected from 'yyyy' to 'yyyy'
        }
        
        if (!dataMap.has(label)) {
          dataMap.set(label, { date: label, 'New Users': 0, 'Logins': 0 });
        }

        currentDate = loginTimeUnit === 'daily' ? subDays(currentDate, -1) : loginTimeUnit === 'monthly' ? new Date(currentDate.setMonth(currentDate.getMonth() + 1)) : new Date(currentDate.setFullYear(currentDate.getFullYear() + 1));
      }

      // Populate Logins from fetched data
      userActivity.forEach(activity => {
        const rawLogin = activity.signed_in_at || activity.created_at;
        if (!rawLogin) {
          return;
        }

        // Support "YYYY-MM-DD HH:mm:ss" by converting to ISO-friendly string
        const loginAt = new Date(rawLogin.replace(" ", "T"));
        if (isNaN(loginAt)) {
          return;
        }

        let label;
        if (loginTimeUnit === 'daily') {
          label = format(loginAt, 'MMM d');
        } else if (loginTimeUnit === 'monthly') {
          label = format(loginAt, 'MMM yyyy');
        } else { // yearly
          label = format(loginAt, 'yyyy');
        }

        // Ensure the entry exists before trying to update it
        if (dataMap.has(label)) {
          const entry = dataMap.get(label);
          entry['Logins'] += 1;
        }
      });

      setLoginChartData(Array.from(dataMap.values()));
    };
    processLoginActivityChartData();
  }, [userActivity, loginDateRange, loginTimeUnit]);

  useEffect(() => {
    // Handle showing the marquee once per 24h
    const now = Date.now();
    const storedStart = localStorage.getItem(ANNOUNCEMENT_KEY);
    const startAt = storedStart ? Number(storedStart) : now;
    const elapsed = now - startAt;
    const duration = 24 * 60 * 60 * 1000;

    // persist the start time so reloads keep showing until 24h passes
    if (!storedStart) {
      localStorage.setItem(ANNOUNCEMENT_KEY, String(startAt));
    }

    if (elapsed < duration) {
      setShowAnnouncement(true);
      setShowToast(true);
      const remaining = duration - elapsed;
      const timer = setTimeout(() => {
        localStorage.removeItem(ANNOUNCEMENT_KEY);
        setShowAnnouncement(false);
      }, remaining);
      return () => clearTimeout(timer);
    } else {
      localStorage.removeItem(ANNOUNCEMENT_KEY);
    }
  }, []);

  const handleDismiss = () => {
    setShowAnnouncement(false);
    setShowToast(true);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={pageBackground}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={pageBackground}>
        <div className="alert alert-danger m-0">{error}</div>
      </div>
    );
  }

  return (
    <div style={pageBackground}>
      <div style={{ width: '100%', maxWidth: '1400px', margin: '0 auto' }}>
        <style>{`
          @keyframes marqueeSlide {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>

        {showAnnouncement && (
          <div
            style={{
              position: 'relative',
              overflow: 'hidden',
              background: 'linear-gradient(90deg, #111827, #1f2937, #0ea5e9)',
              borderRadius: '14px',
              padding: '12px 16px',
              marginBottom: '16px',
              color: '#e2e8f0',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.25)'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <span
                className="badge bg-info text-dark"
                style={{ fontWeight: 700, letterSpacing: '0.02em' }}
              >
                NEW
              </span>
              <div style={{ flex: 1, overflow: 'hidden', position: 'relative', height: '28px' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    whiteSpace: 'nowrap',
                    gap: '30px',
                    animation: 'marqueeSlide 16s linear infinite'
                  }}
                >
                  <span style={{ fontSize: '15px', fontWeight: 600 }}>{announcementText}</span>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline-light"
                style={{ color: '#0b1220', background: '#e2e8f0', border: 'none', fontWeight: 700 }}
                onClick={handleDismiss}
              >
                Close
              </Button>
            </div>
            <div style={{ marginTop: '6px', fontSize: '12px', color: 'rgba(226,232,240,0.75)' }}>
              This message auto-hides after 24 hours.
            </div>
          </div>
        )}

        <ToastContainer position="top-end" className="p-3">
          <Toast
            bg="dark"
            onClose={() => setShowToast(false)}
            show={showToast}
            delay={4000}
            autohide
          >
            <Toast.Header closeButton={false}>
              <strong className="me-auto">Dashboard Updates</strong>
              <small>Just now</small>
            </Toast.Header>
            <Toast.Body style={{ color: '#e2e8f0' }}>
              {showAnnouncement
                ? "New updates banner is visible. Close it if you've got the message."
                : "Banner dismissed for now. It will reappear on reload until 24 hours pass."}
            </Toast.Body>
          </Toast>
        </ToastContainer>
        

        {stats && (
          <div style={{ ...panelStyle, padding: '1.25rem', marginBottom: '1.5rem' }}>
            <Row className="g-3">
              <StatCard title="Total Users" value={stats.totalUsers} icon="bi-people-fill" bgColor="primary" />
              <StatCard title="Paid Users" value={stats.subscribedUsers} icon="bi-person-check-fill" bgColor="success" />
              <StatCard title="Non-Paid Users" value={stats.nonSubscribedUsers} icon="bi-person-x-fill" bgColor="warning" textColor="white" />
              {stats.emailStats && (
                <>
                  <StatCard
                    title="Total Emails"
                    value={` ${stats.emailStats.totalCompleted}/${stats.emailStats.totalProcessed} `}
                    icon="bi-envelope-fill" bgColor="info" textColor="white" />
                  <StatCard
                    title="Today's Emails"
                    value={`${stats.emailStats.todayCompleted}/${stats.emailStats.todayProcessed}`}
                    icon="bi-envelope-check-fill" bgColor="secondary" />
                  <StatCard
                    title="Unique Email Users"
                    value={stats.emailStats.uniqueUsers}
                    icon="bi-person-lines-fill" bgColor="dark" />
                </>
              )}
            </Row>
          </div>
        )}

        <div className="d-flex flex-column flex-xl-row gap-4">
          <Card className="shadow-sm" style={{ ...panelStyle, flex: 1 }}>
            <Card.Body>
              <Card.Title style={{ color: '#0f172a', fontWeight: 800 }}>New Users</Card.Title>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <ButtonGroup size="sm" className="mb-2 mb-md-0">
                  <Button
                    variant="link"
                    style={{
                      background: registrationTimeUnit === 'daily' ? 'linear-gradient(135deg, #2563eb, #22c55e)' : '#f8fafc',
                      border: '1px solid #e5e7eb',
                      color: registrationTimeUnit === 'daily' ? '#0b1220' : '#0f172a',
                      fontWeight: 600,
                    }}
                    onClick={() => setRegistrationTimeUnit('daily')}
                  >
                    Daily
                  </Button>
                  <Button
                    variant="link"
                    style={{
                      background: registrationTimeUnit === 'monthly' ? 'linear-gradient(135deg, #2563eb, #22c55e)' : '#f8fafc',
                      border: '1px solid #e5e7eb',
                      color: registrationTimeUnit === 'monthly' ? '#0b1220' : '#0f172a',
                      fontWeight: 600,
                    }}
                    onClick={() => setRegistrationTimeUnit('monthly')}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant="link"
                    style={{
                      background: registrationTimeUnit === 'yearly' ? 'linear-gradient(135deg, #2563eb, #22c55e)' : '#f8fafc',
                      border: '1px solid #e5e7eb',
                      color: registrationTimeUnit === 'yearly' ? '#0b1220' : '#0f172a',
                      fontWeight: 600,
                    }}
                    onClick={() => setRegistrationTimeUnit('yearly')}
                  >
                    Yearly
                  </Button>
                </ButtonGroup>
                <div className="d-flex align-items-center">
                  <Form.Control
                    type="date"
                    size="sm"
                    value={registrationDateRange.start}
                    onChange={e => setRegistrationDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="me-2"
                    style={{
                      width: '150px',
                      background: '#f8fafc',
                      borderColor: '#e2e8f0',
                      color: '#0f172a',
                    }}
                  />
                  <span className="me-2" style={{ color: '#64748b' }}>to</span>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={registrationDateRange.end}
                    onChange={e => setRegistrationDateRange(prev => ({ ...prev, end: e.target.value }))}
                    style={{
                      width: '150px',
                      background: '#f8fafc',
                      borderColor: '#e2e8f0',
                      color: '#0f172a',
                    }}
                  />
                </div>
              </div>
              <div style={{ height: '400px', background: '#f8fafc', borderRadius: '12px', padding: '0.5rem', border: '1px solid #e5e7eb' }}>
                <UserRegistrationChart chartData={registrationChartData} />
              </div>
            </Card.Body>
          </Card>

          <Card className="shadow-sm" style={{ ...panelStyle, flex: 1 }}>
            <Card.Body>
              <Card.Title style={{ color: '#0f172a', fontWeight: 800 }}>User Login Activity</Card.Title>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <ButtonGroup size="sm" className="mb-2 mb-md-0">
                  <Button
                    variant="link"
                    style={{
                      background: loginTimeUnit === 'daily' ? 'linear-gradient(135deg, #2563eb, #22c55e)' : '#f8fafc',
                      border: '1px solid #e5e7eb',
                      color: loginTimeUnit === 'daily' ? '#0b1220' : '#0f172a',
                      fontWeight: 600,
                    }}
                    onClick={() => setLoginTimeUnit('daily')}
                  >
                    Daily
                  </Button>
                  <Button
                    variant="link"
                    style={{
                      background: loginTimeUnit === 'monthly' ? 'linear-gradient(135deg, #2563eb, #22c55e)' : '#f8fafc',
                      border: '1px solid #e5e7eb',
                      color: loginTimeUnit === 'monthly' ? '#0b1220' : '#0f172a',
                      fontWeight: 600,
                    }}
                    onClick={() => setLoginTimeUnit('monthly')}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant="link"
                    style={{
                      background: loginTimeUnit === 'yearly' ? 'linear-gradient(135deg, #2563eb, #22c55e)' : '#f8fafc',
                      border: '1px solid #e5e7eb',
                      color: loginTimeUnit === 'yearly' ? '#0b1220' : '#0f172a',
                      fontWeight: 600,
                    }}
                    onClick={() => setLoginTimeUnit('yearly')}
                  >
                    Yearly
                  </Button>
                </ButtonGroup>
                <div className="d-flex align-items-center">
                  <Form.Control
                    type="date"
                    size="sm"
                    value={loginDateRange.start}
                    onChange={e => setLoginDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="me-2"
                    style={{
                      width: '150px',
                      background: '#f8fafc',
                      borderColor: '#e2e8f0',
                      color: '#0f172a',
                    }}
                  />
                  <span className="me-2" style={{ color: '#64748b' }}>to</span>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={loginDateRange.end}
                    onChange={e => setLoginDateRange(prev => ({ ...prev, end: e.target.value }))}
                    style={{
                      width: '150px',
                      background: '#f8fafc',
                      borderColor: '#e2e8f0',
                      color: '#0f172a',
                    }}
                  />
                </div>
              </div>
              <div style={{ height: '400px', background: '#f8fafc', borderRadius: '12px', padding: '0.5rem', border: '1px solid #e5e7eb' }}>
                <UserLoginActivityChart chartData={loginChartData} />
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}
